import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { taskAPI } from '../services/api';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ClockIcon,
  UserIcon,
  FlagIcon,
} from '@heroicons/react/24/outline';

const COLUMNS = [
  { id: 'todo', title: 'To Do', color: 'bg-gray-100' },
  { id: 'in_progress', title: 'In Progress', color: 'bg-blue-100' },
  { id: 'in_review', title: 'In Review', color: 'bg-purple-100' },
  { id: 'blocked', title: 'Blocked', color: 'bg-red-100' },
  { id: 'completed', title: 'Completed', color: 'bg-green-100' },
];

const PRIORITY_COLORS = {
  low: 'bg-gray-200 text-gray-700',
  medium: 'bg-yellow-200 text-yellow-800',
  high: 'bg-orange-200 text-orange-800',
  critical: 'bg-red-200 text-red-800',
};

export default function KanbanBoard({ projectId, tasks, onTaskClick, onAddTask }) {
  const queryClient = useQueryClient();
  const [draggedTask, setDraggedTask] = useState(null);

  const updateTaskMutation = useMutation({
    mutationFn: ({ taskId, data }) => taskAPI.updateTask(taskId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['tasks', projectId]);
      toast.success('Task updated!');
    },
    onError: (error) => {
      toast.error(`Failed to update task: ${error.message}`);
    },
  });

  const deleteTaskMutation = useMutation({
    mutationFn: (taskId) => taskAPI.deleteTask(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries(['tasks', projectId]);
      toast.success('Task deleted!');
    },
    onError: (error) => {
      toast.error(`Failed to delete task: ${error.message}`);
    },
  });

  const handleDragStart = (e, task) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, newStatus) => {
    e.preventDefault();
    if (draggedTask && draggedTask.status !== newStatus) {
      updateTaskMutation.mutate({
        taskId: draggedTask.task_id,
        data: { status: newStatus },
      });
    }
    setDraggedTask(null);
  };

  const handleDelete = (e, taskId) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this task?')) {
      deleteTaskMutation.mutate(taskId);
    }
  };

  const getTasksByStatus = (status) => {
    return tasks?.filter((task) => task.status === status) || [];
  };

  return (
    <div className="flex gap-4 overflow-x-auto pb-4">
      {COLUMNS.map((column) => {
        const columnTasks = getTasksByStatus(column.id);

        return (
          <div
            key={column.id}
            className="flex-shrink-0 w-80"
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, column.id)}
          >
            {/* Column Header */}
            <div className={`${column.color} rounded-t-lg p-4 border-b-2 border-gray-300`}>
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-gray-900">{column.title}</h3>
                <span className="bg-white px-2 py-1 rounded-full text-sm font-medium text-gray-700">
                  {columnTasks.length}
                </span>
              </div>
            </div>

            {/* Column Body */}
            <div className="bg-gray-50 rounded-b-lg p-4 min-h-[500px] space-y-3">
              {/* Add Task Button */}
              {column.id === 'todo' && (
                <button
                  onClick={() => onAddTask(column.id)}
                  className="w-full border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-500 hover:bg-blue-50 transition-colors flex items-center justify-center gap-2 text-gray-600 hover:text-blue-600"
                >
                  <PlusIcon className="w-5 h-5" />
                  <span className="font-medium">Add Task</span>
                </button>
              )}

              {/* Tasks */}
              {columnTasks.map((task) => (
                <div
                  key={task.task_id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, task)}
                  onClick={() => onTaskClick(task)}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 cursor-move hover:shadow-md transition-shadow"
                >
                  {/* Priority Badge */}
                  <div className="flex items-start justify-between mb-2">
                    <span
                      className={`px-2 py-1 rounded text-xs font-medium ${PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.medium
                        }`}
                    >
                      {task.priority?.toUpperCase()}
                    </span>
                    <div className="flex gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onTaskClick(task);
                        }}
                        className="p-1 hover:bg-gray-100 rounded"
                      >
                        <PencilIcon className="w-4 h-4 text-gray-600" />
                      </button>
                      <button
                        onClick={(e) => handleDelete(e, task.task_id)}
                        className="p-1 hover:bg-red-100 rounded"
                      >
                        <TrashIcon className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </div>

                  {/* Task Title */}
                  <h4 className="font-semibold text-gray-900 mb-2 line-clamp-2">
                    {task.title}
                  </h4>

                  {/* Task Description */}
                  {task.description && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {task.description}
                    </p>
                  )}

                  {/* Task Metadata */}
                  <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                    {task.category && (
                      <span className="bg-gray-100 px-2 py-1 rounded">
                        {task.category}
                      </span>
                    )}
                    {task.due_date && (
                      <span className="flex items-center gap-1">
                        <ClockIcon className="w-3 h-3" />
                        {new Date(task.due_date).toLocaleDateString('en-GB')}
                      </span>
                    )}
                    {task.assigned_to && (
                      <span className="flex items-center gap-1">
                        <UserIcon className="w-3 h-3" />
                        Assigned
                      </span>
                    )}
                    {task.estimated_hours && (
                      <span className="flex items-center gap-1">
                        <ClockIcon className="w-3 h-3" />
                        {task.estimated_hours}h
                      </span>
                    )}
                  </div>

                  {/* Tags */}
                  {task.tags && task.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {task.tags.map((tag, idx) => (
                        <span
                          key={idx}
                          className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Progress Bar */}
                  {task.progress_percentage > 0 && (
                    <div className="mt-3">
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div
                          className="bg-blue-600 h-1.5 rounded-full transition-all"
                          style={{ width: `${task.progress_percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
